import { HttpTransportType, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import reduxSignalRMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-signalr-middleware";
import { RootState } from "_shared/_stores/root-reducer";
import { getSignalRReconnectDelaysMs } from "_shared/_stores/realtime/signalr-reconnect-policy";
import { AppDispatch } from "_shared/_stores/store-types";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import * as Constants from "config";
import Session from "users/__session/session";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import ProposalSchema from "work/proposals/_schemas/proposal-schema";
import {
  addProposalJSON,
  removeProposal,
  replaceOriginalProposalBuilder,
  replaceUpdatedProposalBuilder,
} from "work/proposals/_store/proposals-redux-slice";
import Proposal from "work/proposals/proposal";

export const proposalHubConnection = new HubConnectionBuilder()
  .configureLogging(LogLevel.Debug)
  .withUrl(`${Constants.apiBaseUrl}/proposalHub`, {
    skipNegotiation: true,
    transport: HttpTransportType.WebSockets,
    accessTokenFactory: () => Session.loadFromStorage(() => {}).authToken?.value ?? "",
  })
  .withAutomaticReconnect(getSignalRReconnectDelaysMs())
  .build();

interface ProposalDeletedEvent {
  id: string;
}

const callbacks = withCallbacks<RootState, AppDispatch>()
  .add("proposal-created", (payload: unknown) => (dispatch, getState) => {
    const incomingProposalJSON = ProposalSchema.validateJSON(payload);
    if (!incomingProposalJSON.id) {
      throw new Error("Received proposal-created event with no proposal ID");
    }
    const session = Session.loadFromStorage(() => {});
    const state = getState();
    const existingProposal = state.proposals.byId[incomingProposalJSON.id];
    if (existingProposal) {
      const incomingLastUpdated = AHDate.fromJSON(incomingProposalJSON.lastUpdated);
      const existingLastUpdated = AHDate.fromJSON(existingProposal.lastUpdated);
      if (existingLastUpdated && incomingLastUpdated && existingLastUpdated.isAfter(incomingLastUpdated)) {
        return; // ignore stale create
      }
    }
    dispatch(
      proposalsApi.util.upsertQueryData(
        "getProposalById",
        { proposalId: incomingProposalJSON.id, session },
        incomingProposalJSON,
      ),
    );
    dispatch(
      proposalsApi.util.invalidateTags([
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "ArchiveCount" },
      ]),
    );
    dispatch(addProposalJSON(incomingProposalJSON));
  })
  .add("proposal-updated", (payload: unknown) => (dispatch, getState) => {
    const incomingProposalIdValue = (payload as { id: string } | null | undefined)?.id;
    if (!incomingProposalIdValue) {
      throw new Error("Received proposal-updated event with no proposal ID");
    }
    const incomingProposalLastUpdated = AHDate.fromJSON((payload as { lastUpdated: string }).lastUpdated);
    if (!incomingProposalLastUpdated) {
      throw new Error("Received proposal-updated event with no lastUpdated timestamp");
    }

    const state = getState();
    const existingProposal = state.proposals.byId[incomingProposalIdValue];
    const existingLastUpdated = AHDate.fromJSON(existingProposal?.lastUpdated) ?? undefined;
    if (existingLastUpdated?.isAfter(incomingProposalLastUpdated)) return; // ignore stale update

    const session = Session.loadFromStorage(() => {});

    dispatch(
      proposalsApi.util.invalidateTags([
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "ArchiveCount" },
      ]),
    );
    const isUnauthorized = (payload as { isUnauthorized: boolean } | null | undefined)?.isUnauthorized ?? false;
    if (isUnauthorized) {
      dispatch(
        proposalsApi.util.upsertQueryData("getProposalById", { proposalId: incomingProposalIdValue, session }, null),
      );
      dispatch(removeProposal(new Guid(incomingProposalIdValue)));
      return;
    }

    const incomingProposalJSON = ProposalSchema.validateJSON(payload);
    dispatch(
      proposalsApi.util.upsertQueryData(
        "getProposalById",
        { proposalId: incomingProposalIdValue, session },
        incomingProposalJSON,
      ),
    );
    dispatch(addProposalJSON(incomingProposalJSON));

    const incomingProposal = Proposal.fromJSON({
      json: incomingProposalJSON,
      currentUser: session.user!,
      options: { withRedline: true },
    });
    if (!incomingProposal?.id || incomingProposal?.isSubmitted) return;

    const incomingBuilder = incomingProposalJSON.userBuilderJSON
      ? (ProposalBuilder.fromJSON({
          json: incomingProposalJSON.userBuilderJSON,
          session,
        }) ?? undefined)
      : undefined;

    const existingOriginalBuilderJSON = state.proposals.builders.original[incomingProposal.id.value];
    const existingOriginalBuilder = ProposalBuilder.fromJSON({
      json: existingOriginalBuilderJSON,
      session,
    });

    const existingUpdatedBuilderJSON = state.proposals.builders.updated[incomingProposal.id.value];
    const existingUpdatedBuilder = ProposalBuilder.fromJSON({
      json: existingUpdatedBuilderJSON,
      session,
    });

    let originalBuilder = existingOriginalBuilder;
    let updatedBuilder = existingUpdatedBuilder;
    updatedBuilder ??= incomingBuilder;
    if (!updatedBuilder) {
      return;
    }
    if (!originalBuilder) {
      console.warn("Original proposal builder missing, setting to updated builder", {
        proposalId: incomingProposal.id.value,
      });
      originalBuilder = updatedBuilder;
    }

    if (
      existingUpdatedBuilder?.lastUpdated &&
      incomingBuilder?.lastUpdated.isAfter(existingUpdatedBuilder.lastUpdated)
    ) {
      originalBuilder = incomingBuilder;
      updatedBuilder = incomingBuilder;
    }

    if (incomingProposal.lastUpdated?.isAfter(updatedBuilder.lastUpdated)) {
      originalBuilder = new ProposalBuilder({
        session,
        startingSpec: incomingProposal.spec,
      });
      updatedBuilder = updatedBuilder.mergeUpdatedSpec(incomingProposal.spec);
    }
    dispatch(
      replaceOriginalProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: originalBuilder,
      }),
    );
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: updatedBuilder ?? originalBuilder,
      }),
    );
  })
  .add("proposal-deleted", (payload: unknown) => (dispatch) => {
    const incomingProposalIdValue = (payload as ProposalDeletedEvent)?.id;
    if (!incomingProposalIdValue) {
      throw new Error("Received proposal-updated event with no proposal ID");
    }
    const session = Session.loadFromStorage(() => {});
    dispatch(
      proposalsApi.util.invalidateTags([
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "ArchiveCount" },
      ]),
    );
    dispatch(
      proposalsApi.util.upsertQueryData("getProposalById", { proposalId: incomingProposalIdValue, session }, null),
    );

    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: undefined,
      }),
    );
    dispatch(removeProposal(new Guid(incomingProposalIdValue)));
  });

export const proposalsSignalRReduxMiddleware = reduxSignalRMiddleware<RootState, AppDispatch>({
  callbacks,
  connection: proposalHubConnection,
  connectionHealth: { key: "proposals", label: "Proposals" },
  shouldConnectionStartImmediately: true,
});
